import { EditorElement } from '@wix/component-protocol/schema';
import { ManifestResolverAPI } from '../../handlers';
import { VirtualElementAPIType } from '../providers/VirtualElement';
import { AbstractManifestAPI } from '../AbstractManifestAPI';

import { CacheMethodResponse } from '../decorators';
import { MethodCacheStrategy } from '../MethodCacheStrategy';

export class ManifestDefaultValuesResolver extends AbstractManifestAPI<
  ManifestResolverAPI,
  VirtualElementAPIType.ManifestResolver
> {
  getDefaultDataValue(dataKey: string) {
    return this.getDefaultDataValues()[dataKey];
  }

  getDefaultCssPropertyValue(styleKey: string) {
    return this.getDefaultStyleValues().cssProperties[styleKey];
  }

  getDefaultCustomCssPropertyValue(styleKey: string) {
    return this.getDefaultStyleValues().cssCustomProperties[styleKey];
  }

  getDefaultStyleValue(styleKey: string) {
    const defaultValues = this.getDefaultStyleValues();

    return (
      defaultValues.cssProperties[styleKey] ??
      defaultValues.cssCustomProperties[styleKey]
    );
  }

  @CacheMethodResponse({
    namespace: 'ManifestDefaultValuesResolver',
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  getDefaultStyleValues() {
    const values: {
      cssProperties: Record<string, any>;
      cssCustomProperties: Record<string, any>;
    } = {
      cssProperties: {},
      cssCustomProperties: {},
    };

    (['cssProperties', 'cssCustomProperties'] as const).forEach((type) => {
      this.virtualManifestOperations
        .getEditorPointers(type)
        .forEach((context) => {
          const mappings = this.virtualManifestOperations.getMappings(
            context.editorPointer,
            type,
            {
              shouldExpandCssProperties: true,
            },
          );

          values[type] = {
            ...(values[type] ?? {}),
            ...this.resolveDefaultStyleValue(Object.keys(mappings), type),
          };
        });
    });

    return values;
  }

  @CacheMethodResponse({
    cacheStrategy: MethodCacheStrategy.Permanent,
  })
  getDefaultDataValues() {
    const values: Record<string, any> = {};

    Object.entries(
      this.virtualManifest.tree.manifest?.editorElement?.data ?? {},
    )?.forEach(([key, value]) => {
      values[key] = value.defaultValue;
    });

    return values;
  }

  private resolveDefaultStyleValue(
    styleKeys: string[],
    type: 'cssProperties' | 'cssCustomProperties',
  ) {
    if (!styleKeys.length) {
      return {};
    }

    const values: Record<string, any> = {};

    const breadcrumbs = [this.node, ...this.node.getParents()];

    const rootEditorPointer = !this.node.isMultipleComponentsRoot()
      ? this.node.getEditorPointer()
      : null;

    breadcrumbs.forEach((node) => {
      const nodeAPI = node.getAPI(
        VirtualElementAPIType.VirtualManifestAPIReadonly,
      );

      const presetDefinitions = nodeAPI.resolver.getPresetDefinitions();

      const preset = Object.keys(presetDefinitions).length
        ? nodeAPI.query.getAppliedPreset()
        : undefined;

      const presetDefinition = preset ? presetDefinitions?.[preset] : undefined;

      const state = nodeAPI.query.getState();
      const designStates = this.manifestServiceFlags.isSpecEnabled(
        'specs.editor-platform.design-states',
      )
        ? nodeAPI.query.getDesignState()
        : [];

      const nodeEditorPointer = !node.isMultipleComponentsRoot()
        ? node.getEditorPointer()
        : null;

      const childPathDelta =
        nodeEditorPointer && rootEditorPointer
          ? rootEditorPointer.innerElementPath?.slice(
              nodeEditorPointer.innerElementPath?.length,
            ) ?? []
          : [];

      if (!childPathDelta.length) {
        styleKeys.forEach((styleKey) => {
          const presetDefaults =
            presetDefinition?.presetDefaults?.[type]?.[styleKey];

          const defaultValue = resolveValueFlow(
            ...designStates.map(
              (ds) => () => presetDefaults?.statesDefaultValues?.[ds],
            ),
            () =>
              state ? presetDefaults?.statesDefaultValues?.[state] : undefined,
            () => presetDefaults?.defaultValue,
            () => {
              const definitions =
                node.getManifest().editorElement?.[type] ?? {};

              return definitions[styleKey]?.defaultValue;
            },
          );

          if (typeof defaultValue !== 'undefined' && defaultValue !== null) {
            values[styleKey] = defaultValue;
          }
        });

        return;
      }

      if (!presetDefinition || !Object.keys(presetDefinition).length) {
        return;
      }

      const overrides = this.resolveChildOverrides(
        presetDefinition,
        childPathDelta,
      );

      if (!overrides) {
        return;
      }

      const stylesOverrides =
        type === 'cssProperties'
          ? overrides.cssProperties
          : overrides.cssCustomProperties;

      if (!stylesOverrides || !Object.keys(stylesOverrides).length) {
        return undefined;
      }

      styleKeys.forEach((styleKey) => {
        const presetOverridesStyleItem = stylesOverrides[styleKey];

        if (!presetOverridesStyleItem) {
          return;
        }

        const defaultValue = resolveValueFlow(
          ...designStates.map(
            (ds) => () => presetOverridesStyleItem.statesDefaultValues?.[ds],
          ),
          () =>
            state
              ? presetOverridesStyleItem.statesDefaultValues?.[state]
              : undefined,
          () => presetOverridesStyleItem.defaultValue,
        );

        if (typeof defaultValue !== 'undefined' && defaultValue !== null) {
          values[styleKey] = defaultValue;
        }
      });
    });

    return values;
  }

  private resolveChildOverrides(
    parentPreset: NonNullable<EditorElement['presets']>[string],
    childPathDelta: string[],
  ) {
    if (!childPathDelta.length) {
      return undefined;
    }

    let presetDefaults = parentPreset.presetDefaults;

    for (const path of childPathDelta) {
      presetDefaults = presetDefaults?.elements?.[path];
      if (!presetDefaults) {
        return undefined;
      }
    }

    return presetDefaults;
  }
}

function resolveValueFlow(...steps: (() => any | undefined)[]) {
  for (const step of steps) {
    const result = step();
    if (result !== undefined && result !== null) {
      return result;
    }
  }
}
